import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import useSWR, { mutate as globalMutate } from 'swr';
import { useOutletContext } from 'react-router-dom';
import { useApiData } from '../../../config/reducer/Modules';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import BreadCrumbs from '../../../component/breadcrumbs/BreadCrumbs';
import Title from '../../../component/title/Title';
import TruckIcon from '../../../image/truckIcon.svg';
import {
    APIGetCurrentFuelPrices,
    APIGetFuelPriceHistory,
    APIGetActiveEvTariffs,
    APIGetEvTariffHistory,
    APICreateEvTariff,
    APIUpdateEvTariff,
    APIArchiveEvTariff,
} from '../../../config/restAPI/FetchAPI';
import {
    buildActiveFuelPriceMap,
    buildActiveFuelDetailsMap,
    buildFuelPriceRows,
    extractFuelPriceData,
    FUEL_TYPE_OPTIONS,
    getDisplayedFuelRows,
    getFuelTypeLabel,
    hasFuelFilters,
} from './fuelPriceMapper';
import {
    extractEvTariffData,
    normalizeEvTariffRows,
    getEvTariffErrorMessage,
    parseEvTariffMongoId,
    excludeArchivedEvRows,
    TARIFF_TYPE_OPTIONS,
    hasEvFilters,
    getDisplayedEvTariffRows,
    getActiveCurrentEvTariffRows,
    shouldPublishNewEvTariffPrice,
    isClosedEvTariffRow,
} from './evTariffMapper';
import EvTariffModal from './EvTariffModal';
import DropDownList from '../../../component/iconDropDown/iconDropDown';
import '../assetsDevice/AssetsDevice.css';
import '../geofence/geofence.css';
import './PriceRegistry.css';

const seperator = [
    <img loading="lazy" src={TruckIcon} alt="live" style={{ display: 'flex', alignItem: 'center' }} />,
    <div style={{ color: '#475467', fontSize: '14px', fontWeight: 500 }}>Fuel Price Registry</div>,
];

const EV_TARIFF_DEDUPE_MS = 5 * 60 * 1000;
const EV_HISTORY_DEDUPE_MS = 60 * 1000;
const EV_ROWS_PER_PAGE = 10;

const TARIFF_TYPE_FILTER_OPTIONS = [
    { value: 'ALL', label: 'All types' },
    ...TARIFF_TYPE_OPTIONS,
];

const activeEvTariffsFetcher = ([, token, agencyId]) => APIGetActiveEvTariffs(token, agencyId);

const evHistoryFetcher = ([, token, agencyId, tariff, dateStart, dateEnd]) => (
    APIGetEvTariffHistory(token, agencyId, {
        tariff: tariff || undefined,
        dateStart: dateStart || undefined,
        dateEnd: dateEnd || undefined,
    })
);

const CheckIcon = () => (
    <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
    </svg>
);

const formatPrice = (value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
    return value.toFixed(2);
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const FUEL_TYPE_FILTER_OPTIONS = [
    { value: 'ALL', label: 'All types' },
    ...FUEL_TYPE_OPTIONS,
];

function PrFilterDropDown({
    options,
    value,
    onChange,
    placeholder = 'Select',
    width = '12rem',
}) {
    const selected = options.find((option) => option.value === value) || null;
    return (
        <div className="pr-filter-dropdown">
            <DropDownList
              placeholder={placeholder}
              selectedValue={selected}
              setSelectedValue={(option) => onChange(option?.value ?? 'ALL')}
              options={options}
              width={width}
              controlHeight={44}
              menuPortal
            />
        </div>
    );
}

const FUEL_ROWS_PER_PAGE = 6;

const CURRENT_SNAPSHOT_DEDUPE_MS = 5 * 60 * 1000;
const HISTORY_DEDUPE_MS = 60 * 1000;

const currentSnapshotFetcher = ([, token, agencyId]) => APIGetCurrentFuelPrices(token, agencyId);

const historyFetcher = ([, token, agencyId, fuelType, dateStart, dateEnd]) => (
    APIGetFuelPriceHistory(token, agencyId, {
        fuelType: fuelType || undefined,
        dateStart: dateStart || undefined,
        dateEnd: dateEnd || undefined,
    })
);

const LockIcon = () => (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

const LiquidFuelIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M6 20V8.5a1.5 1.5 0 011.5-1.5H9V5a1 1 0 011-1h4a1 1 0 011 1v2h1.5A1.5 1.5 0 0118 8.5V20"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M6 20h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="9" y="11" width="6" height="5" rx="1" fill="currentColor" opacity="0.25" />
        <path d="M18 10l2-1.5a1 1 0 011 1.2V14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const EvChargingIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M13 2L5 14h6l-1 8 9-13h-6l1-7z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeLinejoin="round"
        />
    </svg>
);

const ReadOnlyColHeader = ({
    icon, iconVariant, title, unit,
}) => (
    <div className="pr-readonly-col-hdr">
        <div className="pr-readonly-col-hdr-main">
            <span className={`pr-readonly-col-icon pr-readonly-col-icon--${iconVariant}`}>
                {icon}
            </span>
            <span className="pr-readonly-col-title-text">{title}</span>
            <span className="pr-readonly-col-unit">{unit}</span>
            <span className="pr-readonly-col-lock" title="Locked (Super Admin)">
                <LockIcon />
            </span>
        </div>
        <div className="pr-readonly-col-divider" aria-hidden="true" />
    </div>
);

const ReadOnlyLabel = ({ label, hint }) => {
    const tip = hint && hint !== '—' ? String(hint).trim() : '';
    if (!tip) {
        return <span className="pr-readonly-label pr-readonly-label--plain">{label}</span>;
    }
    return (
        <span className="pr-readonly-label">
            <span className="pr-readonly-label-tip" data-tip={tip}>
                <span className="pr-readonly-label-text">{label}</span>
                <span className="pr-readonly-label-hint" aria-hidden="true">i</span>
            </span>
        </span>
    );
};

const ReadOnlyValue = ({ value, ariaLabel }) => (
    <span
      className="pr-readonly-value"
      role="textbox"
      aria-readonly="true"
      aria-label={ariaLabel}
    >
        {value}
    </span>
);

const sanitizeFuelError = (message) => {
    if (!message) return '';
    if (message.includes('/v3/')) return 'Fuel prices are not available yet.';
    return message;
};

const renderReadOnlyView = ({
    fuelLoading,
    fuelError,
    liquidFuels,
    evTariffs,
}) => {
    const displayFuelError = sanitizeFuelError(fuelError);

    if (fuelLoading) {
        return <div className="pr-loading">Loading fuel prices…</div>;
    }

    return (
        <div className="pr-readonly-card">
            {displayFuelError && (
                <div className="pr-readonly-card-hdr">
                    <div className="pr-readonly-card-sub" style={{ color: 'var(--pr-slate-700)' }}>
                        {displayFuelError}
                    </div>
                </div>
            )}
            <div className="pr-readonly-grid">
                <div className="pr-readonly-col">
                    <ReadOnlyColHeader
                      icon={<LiquidFuelIcon />}
                      iconVariant="fuel"
                      title="Liquid Fuel"
                      unit="RM per litre"
                    />
                    {liquidFuels.map(({
                        fuelType, label, price, hint,
                    }) => (
                        <div key={fuelType} className="pr-readonly-row">
                            <ReadOnlyLabel label={label} hint={hint} />
                            <ReadOnlyValue
                              value={formatPrice(price)}
                              ariaLabel={`${label} price`}
                            />
                        </div>
                    ))}
                </div>
                <div className="pr-readonly-col">
                    <ReadOnlyColHeader
                      icon={<EvChargingIcon />}
                      iconVariant="ev"
                      title="EV Charging"
                      unit="RM per kWh"
                    />
                    {evTariffs.map(({
                        tariff, label, rate, hint,
                    }) => (
                        <div key={tariff} className="pr-readonly-row">
                            <ReadOnlyLabel label={label} hint={hint} />
                            <ReadOnlyValue
                              value={formatPrice(rate)}
                              ariaLabel={`${label} tariff`}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const formatEffectivePeriod = (from, to) => {
    const fromLabel = formatDate(from);
    if (!to) return fromLabel;
    return `${fromLabel} – ${formatDate(to)}`;
};

const renderTablePagination = ({
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    onPreviousPage,
    onNextPage,
}) => {
    const rangeStart = (page - 1) * rowsPerPage + 1;
    const rangeEnd = Math.min(page * rowsPerPage, totalCount);

    return (
        <div className="pr-pagination">
            <button
              type="button"
              className="pr-btn pr-btn-ghost pr-btn-sm"
              disabled={page <= 1}
              onClick={onPreviousPage}
            >
                Previous
            </button>
            <span className="pr-pagination-info">
                {rangeStart}
                –
                {rangeEnd}
                {' '}
                of
                {' '}
                {totalCount}
            </span>
            <button
              type="button"
              className="pr-btn pr-btn-ghost pr-btn-sm"
              disabled={page >= totalPages}
              onClick={onNextPage}
            >
                Next
            </button>
        </div>
    );
};

const renderFuelSection = ({
    loading,
    error,
    rows,
    totalCount,
    isFiltered,
    page,
    totalPages,
    onPreviousPage,
    onNextPage,
}) => {
    if (loading) {
        return <div className="pr-loading">Loading fuel prices…</div>;
    }
    if (error) {
        return <div className="pr-empty">{error}</div>;
    }
    if (totalCount === 0) {
        return (
            <div className="pr-empty">
                {isFiltered
                    ? 'No data for the selected range.'
                    : 'No active fuel price entries available.'}
            </div>
        );
    }

    return (
        <>
            <div className="pr-table-scroll" role="region" aria-label="Liquid fuel prices table">
                <table className="pr-dtab pr-dtab--compact">
                    <thead>
                        <tr>
                            <th>Fuel Type</th>
                            <th>Price (RM/L)</th>
                            <th>Effective Date</th>
                            <th>Source</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id} className={row.status === 'ARCHIVED' ? 'archived' : ''}>
                                <td><strong>{getFuelTypeLabel(row.fuelType)}</strong></td>
                                <td className="pr-cell-num pr-mono">{formatPrice(row.price)}</td>
                                <td className="pr-mono">{formatEffectivePeriod(row.effectiveFrom, row.effectiveTo)}</td>
                                <td className="pr-cell-source">{row.source}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {renderTablePagination({
                page,
                totalPages,
                totalCount,
                rowsPerPage: FUEL_ROWS_PER_PAGE,
                onPreviousPage,
                onNextPage,
            })}
        </>
    );
};

const renderEvSection = ({
    loading,
    error,
    rows,
    totalCount,
    isFiltered,
    page,
    totalPages,
    onPreviousPage,
    onNextPage,
    onEditTariff,
}) => {
    if (loading) {
        return <div className="pr-loading">Loading EV tariffs…</div>;
    }
    if (error) {
        return <div className="pr-empty">{error}</div>;
    }
    if (totalCount === 0) {
        return (
            <div className="pr-empty">
                {isFiltered
                    ? 'No data for the selected range.'
                    : 'No EV tariffs configured yet.'}
            </div>
        );
    }

    return (
        <>
            <div className="pr-table-scroll" role="region" aria-label="EV charging tariffs table">
                <table className="pr-dtab pr-dtab--compact">
                    <thead>
                        <tr>
                            <th>Tariff</th>
                            <th>RM/kWh</th>
                            <th>Effective Date</th>
                            <th>Operator</th>
                            <th><span className="pr-sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id} className={row.status === 'ARCHIVED' ? 'archived' : ''}>
                                <td><strong>{row.tariff}</strong></td>
                                <td className="pr-cell-num pr-mono">{formatPrice(Number(row.rate))}</td>
                                <td className="pr-mono">
                                    {formatEffectivePeriod(row.effectiveFrom, row.effectiveTo)}
                                </td>
                                <td className="pr-cell-source">{row.operator}</td>
                                <td>
                                    <button type="button" className="pr-act-btn" onClick={() => onEditTariff(row)}>View</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {renderTablePagination({
                page,
                totalPages,
                totalCount,
                rowsPerPage: EV_ROWS_PER_PAGE,
                onPreviousPage,
                onNextPage,
            })}
        </>
    );
};

const PriceRegistry = () => {
    const token = Cookies.get('jwtToken');
    const agencyId = Cookies.get('agencyUUID');
    const { apiResponse } = useApiData();
    const [navigtionClick, setNavigtionClick, , setNavigationState] = useOutletContext();

    const [fuelTypeFilter, setFuelTypeFilter] = useState('ALL');
    const [fuelDateFrom, setFuelDateFrom] = useState('');
    const [fuelDateTo, setFuelDateTo] = useState('');
    const [fuelPage, setFuelPage] = useState(1);

    const [evTariffFilter, setEvTariffFilter] = useState('ALL');
    const [evDateFrom, setEvDateFrom] = useState('');
    const [evDateTo, setEvDateTo] = useState('');
    const [evPage, setEvPage] = useState(1);

    const [editingTariff, setEditingTariff] = useState(null);
    const [isTariffModalOpen, setIsTariffModalOpen] = useState(false);
    const [tariffSaving, setTariffSaving] = useState(false);
    const [tariffSaveError, setTariffSaveError] = useState('');

    const role = apiResponse?.role;
    const isSystemAdmin = role === 'System Administrator';
    const isReadOnly = !isSystemAdmin;

    const fuelFilters = useMemo(() => ({
        fuelType: fuelTypeFilter,
        dateFrom: fuelDateFrom,
        dateTo: fuelDateTo,
    }), [fuelTypeFilter, fuelDateFrom, fuelDateTo]);

    const isFuelFiltered = useMemo(
        () => hasFuelFilters(fuelFilters),
        [fuelFilters],
    );

    // Current snapshot — long stale time because the payload is tiny
    // and the underlying gazette data changes infrequently.
    const currentSwrKey = (!isFuelFiltered && token)
        ? ['fuel-current', token, agencyId || '']
        : null;
    const {
        data: currentData,
        error: currentError,
        isValidating: currentValidating,
    } = useSWR(currentSwrKey, currentSnapshotFetcher, {
        dedupingInterval: CURRENT_SNAPSHOT_DEDUPE_MS,
        revalidateOnFocus: false,
        refreshInterval: 0,
        shouldRetryOnError: false,
    });

    // History query — keyed on the active filters so changing fuelType or
    // the date range invalidates the cache automatically.
    const historySwrKey = (isFuelFiltered && token)
        ? [
            'fuel-history',
            token,
            agencyId || '',
            fuelTypeFilter === 'ALL' ? '' : fuelTypeFilter,
            fuelDateFrom || '',
            fuelDateTo || '',
        ]
        : null;
    const {
        data: historyData,
        error: historyError,
        isValidating: historyValidating,
    } = useSWR(historySwrKey, historyFetcher, {
        dedupingInterval: HISTORY_DEDUPE_MS,
        revalidateOnFocus: false,
        refreshInterval: 0,
        shouldRetryOnError: false,
    });

    const activeData = isFuelFiltered ? historyData : currentData;
    const activeError = isFuelFiltered ? historyError : currentError;
    const activeValidating = isFuelFiltered ? historyValidating : currentValidating;

    const fuelRows = useMemo(
        () => buildFuelPriceRows(extractFuelPriceData(activeData)),
        [activeData],
    );

    const fuelLoading = !activeData && !activeError && activeValidating;

    const fuelError = useMemo(() => {
        if (!activeError) return '';
        const status = activeError?.response?.status;
        if (status === 401) return 'Session expired. Redirecting to login…';
        return 'Unable to load fuel prices. Please try again.';
    }, [activeError]);

    const activeFuelPrices = useMemo(
        () => buildActiveFuelPriceMap(fuelRows),
        [fuelRows],
    );

    const displayedFuelRows = useMemo(
        () => getDisplayedFuelRows(fuelRows, fuelFilters),
        [fuelRows, fuelFilters],
    );

    const fuelTotalPages = useMemo(
        () => Math.max(1, Math.ceil(displayedFuelRows.length / FUEL_ROWS_PER_PAGE)),
        [displayedFuelRows.length],
    );

    const paginatedFuelRows = useMemo(() => {
        const start = (fuelPage - 1) * FUEL_ROWS_PER_PAGE;
        return displayedFuelRows.slice(start, start + FUEL_ROWS_PER_PAGE);
    }, [displayedFuelRows, fuelPage]);

    const evFilters = useMemo(() => ({
        tariffType: evTariffFilter,
        dateFrom: evDateFrom,
        dateTo: evDateTo,
    }), [evTariffFilter, evDateFrom, evDateTo]);

    const isEvFiltered = useMemo(
        () => hasEvFilters(evFilters),
        [evFilters],
    );

    const evActiveSwrKey = (!isEvFiltered && token)
        ? ['ev-active', token, agencyId || '']
        : null;
    const {
        data: evActiveData,
        error: evActiveError,
        isValidating: evActiveValidating,
        mutate: mutateEvActive,
    } = useSWR(evActiveSwrKey, activeEvTariffsFetcher, {
        dedupingInterval: EV_TARIFF_DEDUPE_MS,
        revalidateOnFocus: false,
        refreshInterval: 0,
        shouldRetryOnError: false,
    });

    const evHistorySwrKey = (isEvFiltered && token)
        ? [
            'ev-history',
            token,
            agencyId || '',
            evTariffFilter === 'ALL' ? '' : evTariffFilter,
            evDateFrom || '',
            evDateTo || '',
        ]
        : null;
    const {
        data: evHistoryData,
        error: evHistoryError,
        isValidating: evHistoryValidating,
        mutate: mutateEvHistory,
    } = useSWR(evHistorySwrKey, evHistoryFetcher, {
        dedupingInterval: EV_HISTORY_DEDUPE_MS,
        revalidateOnFocus: false,
        refreshInterval: 0,
        shouldRetryOnError: false,
    });

    const activeEvData = isEvFiltered ? evHistoryData : evActiveData;
    const activeEvError = isEvFiltered ? evHistoryError : evActiveError;
    const activeEvValidating = isEvFiltered ? evHistoryValidating : evActiveValidating;

    const evRows = useMemo(
        () => normalizeEvTariffRows(extractEvTariffData(activeEvData)),
        [activeEvData],
    );

    const evLoading = !activeEvData && !activeEvError && activeEvValidating;

    const evLoadError = useMemo(() => {
        if (!activeEvError) return '';
        const status = activeEvError?.response?.status;
        if (status === 401) return 'Session expired. Redirecting to login…';
        return 'Unable to load EV tariffs. Please try again.';
    }, [activeEvError]);

    const displayedEvRows = useMemo(
        () => getDisplayedEvTariffRows(evRows, evFilters),
        [evRows, evFilters],
    );

    const evTotalPages = useMemo(
        () => Math.max(1, Math.ceil(displayedEvRows.length / EV_ROWS_PER_PAGE)),
        [displayedEvRows.length],
    );

    const paginatedEvRows = useMemo(() => {
        const start = (evPage - 1) * EV_ROWS_PER_PAGE;
        return displayedEvRows.slice(start, start + EV_ROWS_PER_PAGE);
    }, [displayedEvRows, evPage]);

    const revalidateAllEvTariffs = () => globalMutate(
        (key) => Array.isArray(key) && (
            key[0] === 'ev-active' || key[0] === 'ev-history'
        ),
        undefined,
        { revalidate: true },
    );

    const currentEvTariffsForReadOnly = useMemo(
        () => getActiveCurrentEvTariffRows(
            normalizeEvTariffRows(extractEvTariffData(evActiveData)),
        ),
        [evActiveData],
    );

    const readOnlyCurrentFuelRows = useMemo(
        () => buildFuelPriceRows(extractFuelPriceData(currentData)),
        [currentData],
    );

    const readonlyLiquidFuels = useMemo(() => {
        const detailsMap = buildActiveFuelDetailsMap(readOnlyCurrentFuelRows);
        return FUEL_TYPE_OPTIONS.map(({ value, label }) => ({
            fuelType: value,
            label,
            price: detailsMap[value]?.price,
            hint: detailsMap[value]?.source ?? null,
        }));
    }, [readOnlyCurrentFuelRows]);

    const readonlyEvTariffs = useMemo(() => {
        const rowByTariff = currentEvTariffsForReadOnly.reduce((accumulator, row) => {
            if (row.tariff) accumulator[row.tariff] = row;
            return accumulator;
        }, {});
        return TARIFF_TYPE_OPTIONS.map(({ value, label }) => ({
            tariff: value,
            label,
            rate: rowByTariff[value]?.rate,
            hint: rowByTariff[value]?.operator ?? null,
        }));
    }, [currentEvTariffsForReadOnly]);

    const readOnlyFuelLoading = !currentData && !currentError && currentValidating;

    const readOnlyFuelError = useMemo(() => {
        if (!currentError) return '';
        const status = currentError?.response?.status;
        if (status === 401) return 'Session expired. Redirecting to login…';
        return 'Unable to load fuel prices. Please try again.';
    }, [currentError]);

    useEffect(() => {
        setFuelPage(1);
    }, [fuelTypeFilter, fuelDateFrom, fuelDateTo]);

    useEffect(() => {
        if (fuelPage > fuelTotalPages) {
            setFuelPage(fuelTotalPages);
        }
    }, [fuelPage, fuelTotalPages]);

    useEffect(() => {
        setEvPage(1);
    }, [evTariffFilter, evDateFrom, evDateTo]);

    useEffect(() => {
        if (evPage > evTotalPages) {
            setEvPage(evTotalPages);
        }
    }, [evPage, evTotalPages]);

    const inventoryNavigationList = useMemo(() => ([
        {
            text: 'Assets & Devices',
            value: 1,
            path: '/inventory/assetdevices',
            enabled: (role === 'Agency Administrator' || role === 'Agency Owner' || isSystemAdmin)
                ? true
                : apiResponse?.module?.fleetManagement?.asset,
        },
        {
            text: 'Connectivity',
            value: 4,
            path: '/inventory/connectivity',
            enabled: (role === 'Agency Administrator' || role === 'Agency Owner')
                ? true
                : apiResponse?.module?.fleetManagement?.connectivity,
        },
        {
            text: 'Battery Configurator',
            value: 5,
            path: '/inventory/batteryconfigurator',
            enabled: isSystemAdmin,
        },
        {
            text: 'Vehicle Configurator',
            value: 6,
            path: '/inventory/vehicleconfigurator',
            enabled: isSystemAdmin,
        },
        {
            text: 'Fuel Price Registry',
            value: 7,
            path: '/inventory/priceRegistry',
            enabled: true,
        },
        {
            text: 'Fuel Configurator',
            value: 8,
            path: '/inventory/fuelconfigurator',
            enabled: apiResponse?.role === 'System Administrator',
        },
    ]), [role, apiResponse, isSystemAdmin]);

    useEffect(() => {
        setNavigtionClick(7);
        setNavigationState('inventory');
    }, []);

    const handleAddTariff = () => {
        setEditingTariff(null);
        setTariffSaveError('');
        setIsTariffModalOpen(true);
    };

    const handleEditTariff = (tariff) => {
        setEditingTariff(tariff);
        setTariffSaveError('');
        setIsTariffModalOpen(true);
    };

    const handleSaveTariff = async (tariff) => {
        setTariffSaving(true);
        setTariffSaveError('');
        const body = {
            rate: tariff.rate,
            effectiveFrom: tariff.effectiveFrom,
            operator: tariff.operator,
            status: tariff.status,
        };
        try {
            const publishNewPrice = shouldPublishNewEvTariffPrice(editingTariff, body);
            const updateId = editingTariff?.mongoId || parseEvTariffMongoId(editingTariff?.id);

            if (publishNewPrice) {
                const tariffCode = editingTariff?.tariff || tariff.tariff;
                if (!tariffCode) {
                    setTariffSaveError('Tariff code is required to publish a new rate.');
                    return;
                }
                await APICreateEvTariff(token, agencyId, {
                    ...body,
                    tariff: tariffCode,
                });
            } else if (updateId) {
                await APIUpdateEvTariff(token, agencyId, updateId, body);
            } else if (editingTariff?.id) {
                setTariffSaveError('Unable to update: server record id is missing. Refresh the page and try again.');
                return;
            } else {
                await APICreateEvTariff(token, agencyId, {
                    ...body,
                    tariff: tariff.tariff,
                });
            }
            await revalidateAllEvTariffs();
            setIsTariffModalOpen(false);
            setEditingTariff(null);
        } catch (err) {
            setTariffSaveError(getEvTariffErrorMessage(err));
        } finally {
            setTariffSaving(false);
        }
    };

    const handleDeleteTariff = async () => {
        const mongoId = editingTariff?.mongoId || parseEvTariffMongoId(editingTariff?.id);
        if (!mongoId) {
            setTariffSaveError('Unable to delete: server record id is missing. Refresh the page and try again.');
            return;
        }
        setTariffSaving(true);
        setTariffSaveError('');
        try {
            await APIArchiveEvTariff(token, agencyId, mongoId);
            await revalidateAllEvTariffs();
            setIsTariffModalOpen(false);
            setEditingTariff(null);
        } catch (err) {
            setTariffSaveError(getEvTariffErrorMessage(err));
        } finally {
            setTariffSaving(false);
        }
    };

    return (
        <div className="inventory-container pr-root">
            <ListNavigationTab list={inventoryNavigationList} navigtionClick={navigtionClick} setNavigtionClick={setNavigtionClick} />

            <div className="inventory-header">
                <BreadCrumbs customSeperator={seperator} />
                <Title
                  title={isReadOnly ? 'Fuel Prices' : 'Fuel & Energy Price Registry'}
                  subtitle={isReadOnly
                      ? 'Active Malaysian Fuel Price Registry (read-only)'
                      : 'Immutable price history'}
                  titleSize="1.875rem"
                />
            </div>

            {isReadOnly ? (
                <div className="pr-registry-readonly">
                    {renderReadOnlyView({
                        fuelLoading: readOnlyFuelLoading,
                        fuelError: readOnlyFuelError,
                        liquidFuels: readonlyLiquidFuels,
                        evTariffs: readonlyEvTariffs,
                    })}
                </div>
            ) : (
                <div className="pr-registry-stack">
                {/* ---------- Liquid Fuel Prices ---------- */}
                <div className="pr-detail-card">
                    <div className="pr-detail-hdr pr-detail-hdr--fuel">
                        <div className="pr-detail-hdr-title">
                            <div className="pr-detail-title">
                                Liquid Fuel Prices
                                <small>· immutable history · data.gov.my source</small>
                            </div>
                        </div>
                        <div className="pr-detail-actions">
                            <div className="pr-fuel-filters">
                                <div className="pr-filter-inline">
                                    <span className="pr-filter-label">Fuel type</span>
                                    <PrFilterDropDown
                                      options={FUEL_TYPE_FILTER_OPTIONS}
                                      value={fuelTypeFilter}
                                      onChange={setFuelTypeFilter}
                                      placeholder="All types"
                                      width="12rem"
                                    />
                                </div>
                                <div className="pr-filter-inline pr-filter-inline--range">
                                    <span className="pr-filter-label">Date range</span>
                                    <input
                                      type="date"
                                      className="pr-input pr-filter-date"
                                      value={fuelDateFrom}
                                      onChange={(e) => setFuelDateFrom(e.target.value)}
                                      aria-label="Effective date from"
                                    />
                                    <span className="pr-filter-date-sep">to</span>
                                    <input
                                      type="date"
                                      className="pr-input pr-filter-date"
                                      value={fuelDateTo}
                                      min={fuelDateFrom || undefined}
                                      onChange={(e) => setFuelDateTo(e.target.value)}
                                      aria-label="Effective date to"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {renderFuelSection({
                        loading: fuelLoading,
                        error: fuelError,
                        rows: paginatedFuelRows,
                        totalCount: displayedFuelRows.length,
                        isFiltered: isFuelFiltered,
                        page: fuelPage,
                        totalPages: fuelTotalPages,
                        onPreviousPage: () => setFuelPage((p) => Math.max(1, p - 1)),
                        onNextPage: () => setFuelPage((p) => Math.min(fuelTotalPages, p + 1)),
                    })}
                </div>

                {/* ---------- EV Charging Tariffs ---------- */}
                <div className="pr-detail-card">
                    <div className="pr-detail-hdr pr-detail-hdr--fuel">
                        <div className="pr-detail-hdr-title">
                            <div className="pr-detail-title">
                                EV Charging Tariffs
                                <small>· multi-operator</small>
                            </div>
                        </div>
                        <div className="pr-detail-actions">
                            <div className="pr-fuel-filters">
                                <div className="pr-filter-inline">
                                    <span className="pr-filter-label">Tariff type</span>
                                    <PrFilterDropDown
                                      options={TARIFF_TYPE_FILTER_OPTIONS}
                                      value={evTariffFilter}
                                      onChange={setEvTariffFilter}
                                      placeholder="All types"
                                      width="11rem"
                                    />
                                </div>
                                <div className="pr-filter-inline pr-filter-inline--range">
                                    <span className="pr-filter-label">Date range</span>
                                    <input
                                      type="date"
                                      className="pr-input pr-filter-date"
                                      value={evDateFrom}
                                      onChange={(e) => setEvDateFrom(e.target.value)}
                                      aria-label="Effective date from"
                                    />
                                    <span className="pr-filter-date-sep">to</span>
                                    <input
                                      type="date"
                                      className="pr-input pr-filter-date"
                                      value={evDateTo}
                                      min={evDateFrom || undefined}
                                      onChange={(e) => setEvDateTo(e.target.value)}
                                      aria-label="Effective date to"
                                    />
                                </div>
                            </div>
                            <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" onClick={handleAddTariff}>
                                + Add EV Tariff
                            </button>
                        </div>
                    </div>
                    {renderEvSection({
                        loading: evLoading,
                        error: evLoadError,
                        rows: paginatedEvRows,
                        totalCount: displayedEvRows.length,
                        isFiltered: isEvFiltered,
                        page: evPage,
                        totalPages: evTotalPages,
                        onPreviousPage: () => setEvPage((p) => Math.max(1, p - 1)),
                        onNextPage: () => setEvPage((p) => Math.min(evTotalPages, p + 1)),
                        onEditTariff: handleEditTariff,
                    })}
                </div>
                </div>
            )}

            {isSystemAdmin && isTariffModalOpen && (
                <EvTariffModal
                  initialValue={editingTariff}
                  saving={tariffSaving}
                  serverError={tariffSaveError}
                  onCancel={() => {
                      if (!tariffSaving) {
                          setIsTariffModalOpen(false);
                          setEditingTariff(null);
                          setTariffSaveError('');
                      }
                  }}
                  onSave={handleSaveTariff}
                  onDelete={(editingTariff?.mongoId || parseEvTariffMongoId(editingTariff?.id))
                      ? handleDeleteTariff
                      : undefined}
                />
            )}
        </div>
    );
};

export default PriceRegistry;
